import {
  faObjectsAlignBottom,
  faObjectsAlignCenterHorizontal,
  faObjectsAlignCenterVertical,
  faObjectsAlignLeft,
  faObjectsAlignRight,
  faObjectsAlignTop,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import {
  ALIGNMENT_BUTTON_CLASS,
  ALIGNMENT_TOOLTIP_CONTENT_PROPS,
  OptionButtonGroup,
  type OptionButtonOption,
  SettingRow,
} from '@/components/page-editor/settings-panel/fields';
import { ButtonGroup } from '@/components/ui/button-group';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';
import {
  alignmentOptions,
  backgroundWidthOptions,
  blockAlignOptions,
  insetSizeOptions,
  optionLabelMap,
  spacingOptions,
} from './constants';
import type {
  BackgroundInsetSize,
  BackgroundSpacing,
  BackgroundVerticalAlign,
  BackgroundWidth,
  BlockAlign,
} from './types';

export interface LayoutDraft {
  fullWidth: boolean;
  backgroundSpacing: BackgroundSpacing;
  backgroundInset: boolean;
  backgroundInsetSize: BackgroundInsetSize;
  backgroundVerticalAlign: BackgroundVerticalAlign;
  blockAlign: BlockAlign;
}

interface LayoutSettingsTabProps<T extends LayoutDraft> {
  draft: T;
  applyDraft: (updater: (d: T) => T) => void;
}

const spacingButtonOptions: OptionButtonOption<BackgroundSpacing>[] = spacingOptions.map(
  (value) => ({
    value,
    label: optionLabelMap[value] || value.toUpperCase(),
    ariaLabel: `${value.toUpperCase()} spacing`,
  }),
);

const insetSizeButtonOptions: OptionButtonOption<BackgroundInsetSize>[] = insetSizeOptions.map(
  (value) => ({
    value,
    label: optionLabelMap[value] || value.toUpperCase(),
    ariaLabel: `${value.toUpperCase()} inset`,
  }),
);

const backgroundWidthButtonOptions: OptionButtonOption<BackgroundWidth>[] =
  backgroundWidthOptions.map((value) => ({
    value,
    label: optionLabelMap[value] || value,
    ariaLabel: `${optionLabelMap[value] || value} background width`,
  }));

const VERTICAL_ALIGNMENT_ICONS: Record<BackgroundVerticalAlign, IconDefinition> = {
  top: faObjectsAlignTop,
  center: faObjectsAlignCenterVertical,
  bottom: faObjectsAlignBottom,
};

const HORIZONTAL_ALIGNMENT_ICONS: Record<BlockAlign, IconDefinition> = {
  start: faObjectsAlignLeft,
  center: faObjectsAlignCenterHorizontal,
  end: faObjectsAlignRight,
};

interface AlignmentRowProps<T extends string> {
  title: string;
  description: string;
  options: readonly T[];
  value: T;
  icons: Record<T, IconDefinition>;
  onChange: (value: T) => void;
}

function AlignmentRow<T extends string>({
  title,
  description,
  options,
  value,
  icons,
  onChange,
}: AlignmentRowProps<T>) {
  return (
    <SettingRow title={title} description={description}>
      <ButtonGroup className="w-full">
        {options.map((option) => (
          <IconTooltipButton
            key={option}
            tooltip={optionLabelMap[option]}
            tooltipContentProps={ALIGNMENT_TOOLTIP_CONTENT_PROPS}
            variant={value === option ? 'solid' : 'outline'}
            colorScheme="secondary"
            size="icon"
            className={ALIGNMENT_BUTTON_CLASS}
            onClick={() => onChange(option)}
          >
            <Icon icon={icons[option]} className="size-4" />
          </IconTooltipButton>
        ))}
      </ButtonGroup>
    </SettingRow>
  );
}

export function LayoutSettingsTab<T extends LayoutDraft>({
  draft,
  applyDraft,
}: LayoutSettingsTabProps<T>) {
  return (
    <TabsContent value="settings" className="space-y-3 pt-1">
      <SettingRow title="Full Width" description="Content fills the full width of the page.">
        <Switch
          checked={draft.fullWidth}
          onCheckedChange={(checked) =>
            applyDraft((previous) => ({
              ...previous,
              fullWidth: checked,
            }))
          }
          aria-label="Toggle full width"
        />
      </SettingRow>

      <SettingRow title="Spacing" description="Section height and vertical spacing.">
        <OptionButtonGroup
          options={spacingButtonOptions}
          value={draft.backgroundSpacing}
          buttonClassName="min-w-9 px-2 text-xs font-semibold"
          onChange={(value) =>
            applyDraft((previous) => ({
              ...previous,
              backgroundSpacing: value,
            }))
          }
        />
      </SettingRow>

      <SettingRow
        title="Background Width"
        description="Background fills the section or appears inset."
      >
        <OptionButtonGroup
          options={backgroundWidthButtonOptions}
          value={draft.backgroundInset ? 'inset' : 'fill'}
          buttonClassName="min-w-12 px-2 text-xs font-semibold"
          onChange={(value) =>
            applyDraft((previous) => ({
              ...previous,
              backgroundInset: value === 'inset',
            }))
          }
        />
      </SettingRow>

      {draft.backgroundInset ? (
        <SettingRow
          title="Inset Size"
          description="Control how much the section is inset from the page edges."
        >
          <OptionButtonGroup
            options={insetSizeButtonOptions}
            value={draft.backgroundInsetSize}
            buttonClassName="min-w-9 px-2 text-xs font-semibold"
            onChange={(value) =>
              applyDraft((previous) => ({
                ...previous,
                backgroundInsetSize: value,
              }))
            }
          />
        </SettingRow>
      ) : null}

      <AlignmentRow
        title="Vertical Alignment"
        description="Align section content to top, center, or bottom."
        options={alignmentOptions}
        value={draft.backgroundVerticalAlign}
        icons={VERTICAL_ALIGNMENT_ICONS}
        onChange={(value) =>
          applyDraft((previous) => ({
            ...previous,
            backgroundVerticalAlign: value,
          }))
        }
      />

      <AlignmentRow
        title="Horizontal Alignment"
        description="Align section content to left, center, or right."
        options={blockAlignOptions}
        value={draft.blockAlign}
        icons={HORIZONTAL_ALIGNMENT_ICONS}
        onChange={(value) =>
          applyDraft((previous) => ({
            ...previous,
            blockAlign: value,
          }))
        }
      />
    </TabsContent>
  );
}
